/**
 * SEO Sprint 2 (Feb 2026) — Destination CMS hook.
 *
 * Fetcht de CMS-payload voor een destination slug uit
 *   GET /api/website/destinations/{slug}
 *
 * Falls back op de production-veilige defaults die in de page-wrapper
 * worden meegegeven zodat de UI nooit blank gaat bij een cold start
 * of API-fout.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function useDestinationCms(slug, fallback) {
  const [data, setData] = useState(fallback || null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/destinations/${slug}`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const sections = res?.data?.sections;
        if (sections && typeof sections === "object") {
          setData((prev) => ({
            ...prev,
            display_name: res.data.display_name || prev?.display_name || "",
            country_code: res.data.country_code || prev?.country_code || "",
            url_path: res.data.url_path || prev?.url_path || "",
            page_banner: {
              ...(fallback?.page_banner || prev?.page_banner || {}),
              ...(sections.page_banner || {}),
              badges: Array.isArray(sections.page_banner?.badges)
                ? sections.page_banner.badges
                : (fallback?.page_banner?.badges || prev?.page_banner?.badges || []),
            },
            intro: {
              ...(fallback?.intro || prev?.intro || {}),
              ...(sections.intro || {}),
            },
            service_blocks: {
              items: Array.isArray(sections.service_blocks?.items)
                ? sections.service_blocks.items
                : (fallback?.service_blocks?.items || prev?.service_blocks?.items || []),
            },
            // SEO Sprint 3 — new sections
            process_steps: {
              items: Array.isArray(sections.process_steps?.items)
                ? sections.process_steps.items
                : (fallback?.process_steps?.items || prev?.process_steps?.items || []),
            },
            trust_signals: {
              items: Array.isArray(sections.trust_signals?.items)
                ? sections.trust_signals.items
                : (fallback?.trust_signals?.items || prev?.trust_signals?.items || []),
            },
            faq: {
              items: Array.isArray(sections.faq?.items)
                ? sections.faq.items
                : (fallback?.faq?.items || prev?.faq?.items || []),
            },
            cta_band: {
              ...(fallback?.cta_band || prev?.cta_band || {}),
              ...(sections.cta_band || {}),
            },
            seo: {
              ...(fallback?.seo || prev?.seo || {}),
              ...(sections.seo || {}),
            },
          }));
        }
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return { data: data || fallback, loaded };
}
